import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import InvoiceForm from './InvoiceForm';
import ProjectSalesTaxWarningModal from '../ProjectSalesTaxWarningModal';
import API_BASE from '../../../utils/api.js';
import { hasProjectSalesTaxAssigned } from '../../../utils/projectSalesTax.js';
import usePermissions from '../../../auth/usePermissions';

const normalizeMarkupValue = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = Number(String(value).replace('%', '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default function InvoiceFormPage() {
  const { projectId, invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions, permissionsLoaded } = usePermissions();

  useEffect(() => {
    if (permissionsLoaded && !invoiceId && permissions.createEditInvoicesProposals === false) {
      navigate(`/projects/${projectId}`, { replace: true });
    }
  }, [permissionsLoaded, permissions, projectId, invoiceId, navigate]);

  /* -------------------------------------------------------
     Project details passed via redirect (used for header)
  ------------------------------------------------------- */
  const project = location.state?.project || null;

  /* -------------------------------------------------------
     TimeBilling → Invoice passed items
     Example shape:
     { tbItems: [ { description, quantity, rate, total, employee, activity } ] }
  ------------------------------------------------------- */
  const tbItems = location.state?.tbItems || null;

  /* -------------------------------------------------------
     Passed invoice data (duplicate flows, etc.)
  ------------------------------------------------------- */
  const passedInitial = location.state?.invoice
    ? {
        ...location.state.invoice,
        invoiceNumber: location.state.invoice.invoiceNumber || location.state.invoice.number || '',
      }
    : null;

  const [fetchedInvoice, setFetchedInvoice] = useState(null);
  const [projectInfo, setProjectInfo] = useState(project);
  const [error, setError] = useState(null);
  const taxAlertShownRef = useRef(false);
  const [showTaxWarning, setShowTaxWarning] = useState(false);

  const userId = localStorage.getItem('userId');
  const defaultMarkupValue = normalizeMarkupValue(projectInfo?.defaultMarkup, 25);
  const defaultShippingMarkupValue = normalizeMarkupValue(projectInfo?.defaultMarkup, 0);

  /* -------------------------------------------------------
     Fetch project details if not passed via state
  ------------------------------------------------------- */
  useEffect(() => {
    if (projectInfo || !projectId) return;

    (async () => {
      try {
        const url = `${API_BASE}/api/projects/${userId}/${projectId}`;
        const res = await fetch(url);
        const data = await res.json();
        setProjectInfo(data);
      } catch (err) {
        console.error('Failed to fetch project', err);
      }
    })();
  }, [projectInfo, projectId]);

  useEffect(() => {
    if (invoiceId || !projectInfo || taxAlertShownRef.current) return;
    taxAlertShownRef.current = true;
    if (!hasProjectSalesTaxAssigned(projectInfo)) {
      setShowTaxWarning(true);
    }
  }, [invoiceId, projectInfo]);

  /* -------------------------------------------------------
     Fetch invoice when editing directly by URL
  ------------------------------------------------------- */
  useEffect(() => {
    if (!invoiceId || passedInitial) return;

    (async () => {
      try {
        if (!userId) {
          setError('Missing userId in localStorage');
          return;
        }

        const url = `${API_BASE}/api/invoices/${encodeURIComponent(
          userId
        )}/${encodeURIComponent(projectId)}/${encodeURIComponent(invoiceId)}`;

        const res = await fetch(url);
        if (!res.ok) {
          const msg = `Failed to fetch invoice (status ${res.status})`;
          console.error(msg);
          setError(msg);
          return;
        }

        const data = await res.json();

        setFetchedInvoice({
          ...data,
          invoiceNumber: data.invoiceNumber || '',
        });
      } catch (e) {
        console.error('Failed to fetch invoice', e);
        setError("This invoice couldn't be loaded. Please refresh the page or return to the project.");
      }
    })();
  }, [invoiceId, projectId, passedInitial]);

  /* -------------------------------------------------------
     Effective initial invoice state
     Priority:
       1. passedInitial   (duplicate flows)
       2. fetched invoice (editing)
       3. new invoice with tbItems preloaded (if applicable)
  ------------------------------------------------------- */
  let effectiveInitial = passedInitial || fetchedInvoice;

  // If this is a fresh invoice created from TimeBilling, prefill line items
  if (!invoiceId && tbItems && !effectiveInitial) {
    effectiveInitial = {
      lineItems: tbItems.map((it) => ({
        name: it.description,
        description: `${it.employee} - ${it.activity}`,
        qty: it.quantity,
        tradePrice: it.rate,
        clientUnit: it.rate,
        lineTotal: it.total,
        lineMerch: it.total,
        unitType: 'Each',
        type: 'service',
        shipping: 0,
        prepaidTax: 0,
        markup: { type: '%', value: defaultMarkupValue },
        shippingMarkup: { type: '%', value: defaultShippingMarkupValue },
        deposit: 0,
        taxable: true,
        shippingTaxable: false,
      })),
    };
  }

  return (
    <div className="invoice-page">
      {error && <div style={{ padding: 12, color: 'crimson' }}>{error}</div>}

      <InvoiceForm
        project={projectInfo}
        initial={effectiveInitial}
        onClose={() => navigate(`/projects/${projectId}/invoices`)}
        onSaved={() => navigate(`/projects/${projectId}/invoices`)}
      />

      <ProjectSalesTaxWarningModal
        isOpen={showTaxWarning}
        docLabel="invoice"
        onClose={() => setShowTaxWarning(false)}
        onEditProject={() =>
          navigate(`/projects/${encodeURIComponent(projectId)}/edit`, {
            state: { project: projectInfo },
          })
        }
      />
    </div>
  );
}
